// src/pages/orders/adminProductsPage.js — All products (catalog CMS).
//
// Cloned from the Saleor dashboard's product list, which this replaces: header +
// Create Product, search, filters, and a grid/list toggle. The toggle persists to
// localStorage under Saleor's own key and vocabulary (productListViewType =
// tile | datagrid) so the habit carries over from the tool people already use.
//
// Search and filtering are CLIENT-SIDE. adminProducts takes no filter arguments,
// and at six products fetching the page and filtering in memory is honest and
// instant. Past a few hundred products this needs to move server-side — the cost
// is a payload, not a redesign.
//
// REQUIRES ENGINE v41: the query selects `channels`, absent on v40.
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardBody } from "../../components/card/card.jsx";
import { fetchAdminProducts } from "../../actions/commerce_admin_actions";

const VIEW_KEY = "productListViewType";

function money(amount, currency) {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
}

// Price shown as a range, because a product with three variants at one price
// should read "$69.99", not "$69.99 – $69.99".
function priceRange(product, channelSlug) {
  const amounts = (product.variants || [])
    .map((v) => (v.channels || []).find((c) => c.channel === channelSlug))
    .filter((c) => c && c.amount != null);
  if (!amounts.length) return { text: "—", muted: true };
  const nums = amounts.map((c) => c.amount);
  const cur = amounts[0].currency;
  const lo = Math.min(...nums), hi = Math.max(...nums);
  return { text: lo === hi ? money(lo, cur) : `${money(lo, cur)} – ${money(hi, cur)}`, muted: false };
}

// "sold in N of M" — a variant counts a channel as live only when the listing is
// switched on, which is the same thing the storefront asks about.
function channelSummary(product, channels) {
  const live = channels.filter((slug) =>
    (product.variants || []).some((v) => (v.channels || []).some((c) => c.channel === slug && c.available)));
  return { live: live.length, total: channels.length, slugs: live };
}

function StatusDot({ on, title }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 6,
        background: on ? "var(--bs-teal, #20c997)" : "rgba(255,255,255,.35)",
      }}
    />
  );
}

function Thumb({ product, size }) {
  const first = (product.media || [])[0];
  const style = {
    width: size, height: size, objectFit: "cover", borderRadius: 8,
    background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)",
  };
  if (!first) {
    return (
      <div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span className="text-inverse text-opacity-25 small">no image</span>
      </div>
    );
  }
  return first.type === "VIDEO"
    ? <video src={first.url} style={style} muted />
    : <img src={first.url} alt={first.alt || product.name} style={style} />;
}

function GridView({ products, channels, onOpen }) {
  return (
    <div className="row g-3">
      {products.map((p) => {
        const cs = channelSummary(p, channels);
        return (
          <div className="col-6 col-md-4 col-xl-3 col-xxl-2" key={p.id}>
            <div role="button" tabIndex={0} onClick={() => onOpen(p)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); } }}>
              <div style={{ aspectRatio: "1 / 1", width: "100%" }}>
                <Thumb product={p} size="100%" />
              </div>
              <div className="mt-2 small text-inverse text-opacity-50">
                <StatusDot on={p.isPublished} title={p.isPublished ? "Published" : "Draft"} />
                {p.productType || "—"}
              </div>
              <div className="text-inverse">{p.name}</div>
              <div className="small text-inverse text-opacity-50">
                {cs.live} of {cs.total} channels · {p.variants.length} variant{p.variants.length === 1 ? "" : "s"}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ListView({ products, channels, priceChannel, onOpen }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead>
          <tr>
            <th style={{ width: 64 }}></th>
            <th>Product</th>
            <th>Availability</th>
            <th>Variants</th>
            <th>Price</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => {
            const cs = channelSummary(p, channels);
            const pr = priceRange(p, priceChannel);
            return (
              <tr key={p.id} role="button" onClick={() => onOpen(p)}>
                <td><Thumb product={p} size={40} /></td>
                <td>
                  <div className="text-inverse">{p.name}</div>
                  <div className="small text-inverse text-opacity-50">/{p.slug}</div>
                </td>
                <td className="small">
                  <StatusDot on={cs.live > 0} />
                  {cs.live} of {cs.total} channels
                </td>
                <td className="small text-inverse text-opacity-75">{p.variants.length}</td>
                <td className={pr.muted ? "text-inverse text-opacity-25" : ""}>{pr.text}</td>
                <td className="small">
                  <StatusDot on={p.isPublished} />
                  {p.isPublished ? "Published" : "Draft"}
                </td>
              </tr>
            );
          })}
          {products.length === 0 ? (
            <tr><td colSpan="6" className="text-center text-inverse text-opacity-50 py-4">No products match.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminProducts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminProducts, adminLoading, adminError } = useSelector((s) => s.commerceAdmin);

  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "tile");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");        // all | published | draft
  const [channelFilter, setChannelFilter] = useState("all");
  const [priceChannel, setPriceChannel] = useState(null);

  useEffect(() => { dispatch(fetchAdminProducts()); }, [dispatch]);
  useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);

  // Channels come from the products themselves — the engine reports every channel
  // that exists on every variant, so there is no separate channels query to make.
  const channels = useMemo(() => {
    const seen = new Map();
    for (const p of adminProducts) {
      for (const v of p.variants || []) {
        for (const c of v.channels || []) if (!seen.has(c.channel)) seen.set(c.channel, c.name || c.channel);
      }
    }
    return [...seen.entries()].map(([slug, name]) => ({ slug, name }));
  }, [adminProducts]);

  useEffect(() => {
    if (!priceChannel && channels.length) setPriceChannel(channels[0].slug);
  }, [channels, priceChannel]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return adminProducts.filter((p) => {
      if (status === "published" && !p.isPublished) return false;
      if (status === "draft" && p.isPublished) return false;
      if (channelFilter !== "all") {
        const live = (p.variants || []).some((v) => (v.channels || []).some((c) => c.channel === channelFilter && c.available));
        if (!live) return false;
      }
      if (!q) return true;
      // SKU is searchable because it is what an operator has in hand from an order
      // or a packing slip, and it is not visible anywhere else on this page.
      return p.name.toLowerCase().includes(q)
        || (p.slug || "").toLowerCase().includes(q)
        || (p.variants || []).some((v) => (v.sku || "").toLowerCase().includes(q));
    });
  }, [adminProducts, search, status, channelFilter]);

  const open = (p) => navigate(`/orders/products/${p.id}`);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="page-header mb-1">All products</h1>
          <div className="text-inverse text-opacity-50">Catalog &amp; inventory — served by the commerce engine</div>
        </div>
        <button className="btn btn-theme" onClick={() => navigate("/orders/products/new")}>Create Product</button>
      </div>

      {adminError ? <div className="alert alert-danger">{adminError}</div> : null}

      <Card className="mb-3">
        <CardBody className="d-flex flex-wrap gap-2 align-items-center">
          <input className="form-control" style={{ maxWidth: 260 }} placeholder="Search products, slug or SKU…"
            value={search} onChange={(e) => setSearch(e.target.value)} />

          <select className="form-select" style={{ maxWidth: 150 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>

          <select className="form-select" style={{ maxWidth: 200 }} value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}>
            <option value="all">All channels</option>
            {channels.map((c) => <option key={c.slug} value={c.slug}>Sold in {c.name}</option>)}
          </select>

          {view === "datagrid" && channels.length > 1 ? (
            <select className="form-select" style={{ maxWidth: 200 }} value={priceChannel || ""} onChange={(e) => setPriceChannel(e.target.value)}>
              {channels.map((c) => <option key={c.slug} value={c.slug}>Price in {c.name}</option>)}
            </select>
          ) : null}

          <div className="ms-auto btn-group">
            <button className={`btn btn-sm ${view === "datagrid" ? "btn-theme" : "btn-outline-theme"}`}
              onClick={() => setView("datagrid")} title="List view">List</button>
            <button className={`btn btn-sm ${view === "tile" ? "btn-theme" : "btn-outline-theme"}`}
              onClick={() => setView("tile")} title="Grid view">Grid</button>
          </div>
        </CardBody>
      </Card>

      {adminLoading && adminProducts.length === 0 ? (
        <div className="text-center py-5 text-inverse text-opacity-50">Loading…</div>
      ) : view === "tile" ? (
        <GridView products={filtered} channels={channels.map((c) => c.slug)} onOpen={open} />
      ) : (
        <Card><CardBody className="p-0">
          <ListView products={filtered} channels={channels.map((c) => c.slug)} priceChannel={priceChannel} onOpen={open} />
        </CardBody></Card>
      )}

      <div className="mt-3 small text-inverse text-opacity-50">
        {filtered.length} of {adminProducts.length} products
      </div>
    </div>
  );
}
